import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch, RootStateType } from 'App/store';
import {
  setState,
  resetState,
} from 'commons/store/AiGeneration/aiGenerationSlice';
import { type AiFieldResetOptions } from './types';
import { checkIsAiGeneratedForPath } from './utils';

/**
 * Manages AI-generated state for a component, providing read and write capabilities.
 * Use this hook to access and manage AI-generated values stored in Redux.
 *
 * @example
 * const { aiGeneratedValues, isAiGenerated, reset } = useAiGeneratedValues<string>("component.id", "path");
 */
function useAiGeneratedValues<T = unknown>(componentId: string, path?: string) {
  const dispatch = useAppDispatch();

  const aiGeneratedValues = useSelector(
    (state: RootStateType) => state.aiGeneration[componentId] as T | undefined,
  );

  const isAiGenerated = useMemo(
    () => checkIsAiGeneratedForPath(aiGeneratedValues, path),
    [aiGeneratedValues, path],
  );

  const setAiGeneratedValues = useCallback(
    (values: T | ((prevValues: T) => T)) => {
      dispatch(setState({ componentId, value: values }));
    },
    [dispatch, componentId],
  );

  /**
   * Resets the AI-generated value for the component.
   */
  const resetAiGeneratedValues = useCallback(
    (options?: AiFieldResetOptions<T>) => {
      const targetPath = options?.overridePath || path;
      if (!checkIsAiGeneratedForPath(aiGeneratedValues, targetPath)) return;

      if (options?.handler) {
        dispatch((innerDispatch, getState) => {
          const state = getState();
          const currentValue = state.aiGeneration[componentId] as T;
          const newValue = options?.handler?.(currentValue);
          innerDispatch(
            setState({
              componentId,
              value: newValue,
            }),
          );
        });
      } else {
        dispatch(
          resetState({
            componentId,
            path: targetPath,
          }),
        );
      }
    },
    [aiGeneratedValues, componentId, path, dispatch],
  );

  return {
    aiGeneratedValues,
    isAiGenerated,
    setAiGeneratedValues,
    resetAiGeneratedValues,
  } as const;
}

export { useAiGeneratedValues };
export default useAiGeneratedValues;
